// #region Imports
import { createSelector, createSlice, SerializedError } from "@reduxjs/toolkit";
import PaginatedResponse, { type Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { bookmarksApi } from "marketplace/bookmarks/_api/bookmark-api";
import { BookmarkedCompanyJSON } from "marketplace/bookmarks/company-bookmarks/_schema/bookmarked-company-schema";
import type IndividualBookmarkQuery from "marketplace/bookmarks/individual-bookmarks/_api/queries/individual-bookmark-query";
import { BookmarkedIndividualListItemJSON } from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-list-item-schema";
import { BookmarkedIndividualJSON } from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-schema";
import BookmarkedIndividualListItem from "marketplace/bookmarks/individual-bookmarks/bookmarked-individual-list-item";
import { BookmarkedTeamJSON } from "marketplace/bookmarks/team-bookmarks/_schema/bookmarked-team-schema";
// #endregion

// #region Store State Type
/**
 * `individualBookmarksByQuery` holds the pages loaded for each search of the caller's individual
 * bookmarks. The server pages the list, so this slice never holds the full list: a person who is
 * not on a loaded page may still be bookmarked. `individualBookmarkByIndividualId` holds what the
 * session knows about one person: their bookmark, null (not bookmarked), or no entry (not asked
 * yet), and in `failed` the people whose last ask failed. Ask with `useIndividualBookmarkStatus`.
 * `failedPage` holds, for each search, the page whose request failed, until the next request.
 * `companyBookmarkByCompanyId` holds the same for one company. The company bookmarks table reads its
 * pages from RTK Query, so this slice keeps no company page. Ask with `useCompanyBookmarkStatus`.
 * `teamBookmarkByTeamId` holds the same for one team, and the team bookmarks table reads its pages
 * from RTK Query too. Ask with `useTeamBookmarkStatus`.
 */
/** The page whose request failed last for a search, so that a retry asks for that page again. */
export type FailedIndividualBookmarkPage = {
  pageIndex: number;
  append: boolean;
};

export type BookmarksState = {
  companyBookmarkByCompanyId: {
    entries: Record<string, BookmarkedCompanyJSON | null>;
    failed: Record<string, true>;
  };
  teamBookmarkByTeamId: {
    entries: Record<string, BookmarkedTeamJSON | null>;
    failed: Record<string, true>;
  };
  individualBookmarksByQuery: {
    entries: Record<string, Paginated<BookmarkedIndividualListItemJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
    failedPage: Record<string, FailedIndividualBookmarkPage | null>;
  };
  individualBookmarkByIndividualId: {
    entries: Record<string, BookmarkedIndividualJSON | null>;
    failed: Record<string, true>;
  };
};
// #endregion

// #region Initial State
const initialState: BookmarksState = {
  companyBookmarkByCompanyId: {
    entries: {},
    failed: {},
  },
  teamBookmarkByTeamId: {
    entries: {},
    failed: {},
  },
  individualBookmarksByQuery: {
    entries: {},
    loading: {},
    error: {},
    failedPage: {},
  },
  individualBookmarkByIndividualId: {
    entries: {},
    failed: {},
  },
};
// #endregion

// #region Company bookmark helpers
function toBookmarkedCompanyJSON({ id, createdDate, companyId }: BookmarkedCompanyJSON): BookmarkedCompanyJSON {
  return { id, createdDate, companyId };
}

/**
 * Records an authoritative answer about one company: its bookmark, or null when it has none. An
 * answer replaces a failed ask, so the button acts on the bookmark rather than retrying.
 */
function setCompanyBookmarkStatus(state: BookmarksState, companyId: string, bookmark: BookmarkedCompanyJSON | null): void {
  state.companyBookmarkByCompanyId.entries[companyId] = bookmark && toBookmarkedCompanyJSON(bookmark);
  delete state.companyBookmarkByCompanyId.failed[companyId];
}
// #endregion

// #region Team bookmark helpers
function toBookmarkedTeamJSON({ id, createdDate, teamId }: BookmarkedTeamJSON): BookmarkedTeamJSON {
  return { id, createdDate, teamId };
}

/**
 * Records an authoritative answer about one team: its bookmark, or null when it has none. An
 * answer replaces a failed ask, so the button acts on the bookmark rather than retrying.
 */
function setTeamBookmarkStatus(state: BookmarksState, teamId: string, bookmark: BookmarkedTeamJSON | null): void {
  state.teamBookmarkByTeamId.entries[teamId] = bookmark && toBookmarkedTeamJSON(bookmark);
  delete state.teamBookmarkByTeamId.failed[teamId];
}
// #endregion

// #region Individual bookmark helpers
function toBookmarkedIndividualJSON({ id, createdDate, individualId }: BookmarkedIndividualJSON): BookmarkedIndividualJSON {
  return { id, createdDate, individualId };
}

/** Marks the person as not bookmarked, removes the row from every loaded page, and lowers the totals. */
function removeIndividualBookmarkJSON(state: BookmarksState, bookmarkId: string, individualId: string): void {
  state.individualBookmarkByIndividualId.entries[individualId] = null;

  for (const page of Object.values(state.individualBookmarksByQuery.entries)) {
    const remaining = page.data.filter((item) => item.id !== bookmarkId && item.individualId !== individualId);
    if (remaining.length === page.data.length) continue;
    page.data = remaining;
    page.headers.totalElements = Math.max(0, page.headers.totalElements - 1);
    page.headers.totalPages = page.headers.pageSize > 0
      ? Math.ceil(page.headers.totalElements / page.headers.pageSize)
      : page.headers.totalPages;
  }
}

function mergeIndividualBookmarkPages(
  current: Paginated<BookmarkedIndividualListItemJSON> | undefined,
  next: Paginated<BookmarkedIndividualListItemJSON>,
): Paginated<BookmarkedIndividualListItemJSON> {
  if (!current) return next;

  const merged = [...current.data];
  const indexesById = new Map(merged.map((item, index) => [item.id, index]));
  for (const item of next.data) {
    const existingIndex = indexesById.get(item.id);
    if (existingIndex === undefined) {
      indexesById.set(item.id, merged.length);
      merged.push(item);
    } else {
      merged[existingIndex] = item;
    }
  }

  return { data: merged, headers: next.headers };
}
// #endregion

// #region Slice
export const bookmarksSlice = createSlice({
  name: "bookmarks",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // #region RTK Query matchers
    builder.addMatcher(bookmarksApi.endpoints.getCompanyBookmarks.matchFulfilled, (state, action) => {
      for (const item of action.payload.data) {
        setCompanyBookmarkStatus(state, item.companyId, item);
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.getCompanyBookmarksByCompanyIds.matchPending, (state, action) => {
      for (const companyId of action.meta.arg.originalArgs.companyIds) {
        delete state.companyBookmarkByCompanyId.failed[companyId.value];
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.getCompanyBookmarksByCompanyIds.matchRejected, (state, action) => {
      // A condition rejection is a request RTK Query did not send, because the same one is in
      // flight. That one answers.
      if (action.meta.condition) return;
      for (const companyId of action.meta.arg.originalArgs.companyIds) {
        state.companyBookmarkByCompanyId.failed[companyId.value] = true;
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.getCompanyBookmarksByCompanyIds.matchFulfilled, (state, action) => {
      for (const bookmark of action.payload.found) {
        setCompanyBookmarkStatus(state, bookmark.companyId, bookmark);
      }
      // "missing": the company is not bookmarked. The caller asks about their own bookmarks, so the
      // server never refuses one; if it did, the status stays unknown rather than "not bookmarked".
      for (const unavailable of action.payload.unavailable) {
        if (unavailable.reason === "missing") {
          setCompanyBookmarkStatus(state, unavailable.id, null);
        }
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.createCompanyBookmark.matchFulfilled, (state, action) => {
      setCompanyBookmarkStatus(state, action.payload.companyId, action.payload);
    });

    builder.addMatcher(bookmarksApi.endpoints.deleteCompanyBookmark.matchFulfilled, (state, action) => {
      setCompanyBookmarkStatus(state, action.meta.arg.originalArgs.companyId, null);
    });

    builder.addMatcher(bookmarksApi.endpoints.getTeamBookmarks.matchFulfilled, (state, action) => {
      for (const item of action.payload.data) {
        setTeamBookmarkStatus(state, item.teamId, item);
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.getTeamBookmarksByTeamIds.matchPending, (state, action) => {
      for (const teamId of action.meta.arg.originalArgs.teamIds) {
        delete state.teamBookmarkByTeamId.failed[teamId.value];
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.getTeamBookmarksByTeamIds.matchRejected, (state, action) => {
      // A condition rejection is a request RTK Query did not send, because the same one is in
      // flight. That one answers.
      if (action.meta.condition) return;
      for (const teamId of action.meta.arg.originalArgs.teamIds) {
        state.teamBookmarkByTeamId.failed[teamId.value] = true;
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.getTeamBookmarksByTeamIds.matchFulfilled, (state, action) => {
      for (const bookmark of action.payload.found) {
        setTeamBookmarkStatus(state, bookmark.teamId, bookmark);
      }
      // "missing": the team is not bookmarked. The caller asks about their own bookmarks, so the
      // server never refuses one; if it did, the status stays unknown rather than "not bookmarked".
      for (const unavailable of action.payload.unavailable) {
        if (unavailable.reason === "missing") {
          setTeamBookmarkStatus(state, unavailable.id, null);
        }
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.createTeamBookmark.matchFulfilled, (state, action) => {
      setTeamBookmarkStatus(state, action.payload.teamId, action.payload);
    });

    builder.addMatcher(bookmarksApi.endpoints.deleteTeamBookmark.matchFulfilled, (state, action) => {
      setTeamBookmarkStatus(state, action.meta.arg.originalArgs.teamId, null);
    });

    builder.addMatcher(bookmarksApi.endpoints.getIndividualBookmarks.matchPending, (state, action) => {
      const cacheKey = action.meta.arg.originalArgs.query.cacheKey;
      state.individualBookmarksByQuery.loading[cacheKey] = true;
      state.individualBookmarksByQuery.error[cacheKey] = null;
      state.individualBookmarksByQuery.failedPage[cacheKey] = null;
    });

    builder.addMatcher(bookmarksApi.endpoints.getIndividualBookmarks.matchFulfilled, (state, action) => {
      const { query, append } = action.meta.arg.originalArgs;
      const cacheKey = query.cacheKey;
      for (const item of action.payload.data) {
        state.individualBookmarkByIndividualId.entries[item.individualId] = toBookmarkedIndividualJSON(item);
      }
      state.individualBookmarksByQuery.entries[cacheKey] = append
        ? mergeIndividualBookmarkPages(state.individualBookmarksByQuery.entries[cacheKey], action.payload)
        : action.payload;
      state.individualBookmarksByQuery.loading[cacheKey] = false;
    });

    builder.addMatcher(bookmarksApi.endpoints.getIndividualBookmarks.matchRejected, (state, action) => {
      // A condition rejection is a request RTK Query did not send, because the same one is in
      // flight. That one answers, and it is still loading.
      if (action.meta.condition) return;
      const { query, append } = action.meta.arg.originalArgs;
      const cacheKey = query.cacheKey;
      state.individualBookmarksByQuery.loading[cacheKey] = false;
      state.individualBookmarksByQuery.error[cacheKey] = action.error;
      state.individualBookmarksByQuery.failedPage[cacheKey] = {
        pageIndex: query.paginationParameters.pageIndex,
        append: append ?? false,
      };
    });

    builder.addMatcher(bookmarksApi.endpoints.getIndividualBookmarksByIndividualIds.matchPending, (state, action) => {
      for (const individualId of action.meta.arg.originalArgs.individualIds) {
        delete state.individualBookmarkByIndividualId.failed[individualId.value];
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.getIndividualBookmarksByIndividualIds.matchRejected, (state, action) => {
      // A condition rejection is a request RTK Query did not send, because the same one is in
      // flight. That one answers.
      if (action.meta.condition) return;
      for (const individualId of action.meta.arg.originalArgs.individualIds) {
        state.individualBookmarkByIndividualId.failed[individualId.value] = true;
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.getIndividualBookmarksByIndividualIds.matchFulfilled, (state, action) => {
      for (const bookmark of action.payload.found) {
        state.individualBookmarkByIndividualId.entries[bookmark.individualId] = toBookmarkedIndividualJSON(bookmark);
      }
      // "missing": the person is not bookmarked. The caller asks about their own bookmarks, so the
      // server never refuses one; if it did, the status stays unknown rather than "not bookmarked".
      for (const unavailable of action.payload.unavailable) {
        if (unavailable.reason === "missing") {
          state.individualBookmarkByIndividualId.entries[unavailable.id] = null;
        }
      }
    });

    builder.addMatcher(bookmarksApi.endpoints.createIndividualBookmark.matchFulfilled, (state, action) => {
      state.individualBookmarkByIndividualId.entries[action.payload.individualId] = toBookmarkedIndividualJSON(action.payload);
    });

    builder.addMatcher(bookmarksApi.endpoints.deleteIndividualBookmark.matchFulfilled, (state, action) => {
      const { bookmarkId, individualId } = action.meta.arg.originalArgs;
      removeIndividualBookmarkJSON(state, bookmarkId, individualId);
    });
    // #endregion
  },
});
// #endregion

// #region Exports

// #region Selectors

// companyBookmarkByCompanyId
/** The bookmark: bookmarked. Null: not bookmarked. Undefined: not asked yet. */
export const getCompanyBookmarkByCompanyId = (companyId?: Guid) =>
  (state: RootState): BookmarkedCompanyJSON | null | undefined =>
    companyId ? state.bookmarks.companyBookmarkByCompanyId.entries[companyId.value] : undefined;

/** True when the last ask about this company failed, and nothing has asked again. */
export const getHasCompanyBookmarkStatusFailed = (companyId?: Guid) => (state: RootState): boolean =>
  companyId ? state.bookmarks.companyBookmarkByCompanyId.failed[companyId.value] ?? false : false;

// individualBookmarksByQuery
const selectIndividualBookmarksByQueryKey = createSelector(
  [(state: RootState) => state.bookmarks.individualBookmarksByQuery.entries, (_: RootState, cacheKey: string) => cacheKey],
  (entries, cacheKey) => {
    const page = entries[cacheKey];
    if (!page) return PaginatedResponse.empty<BookmarkedIndividualListItem>();
    return PaginatedResponse.from(BookmarkedIndividualListItem.fromJSONArray(page.data), page.headers);
  },
);

/** The pages loaded so far for this search, merged in server order. */
export const getIndividualBookmarksByQuery = (query: IndividualBookmarkQuery) => (state: RootState) =>
  selectIndividualBookmarksByQueryKey(state, query.cacheKey);

export const getIsLoadingIndividualBookmarksByQuery = (query: IndividualBookmarkQuery) => (state: RootState) =>
  state.bookmarks.individualBookmarksByQuery.loading[query.cacheKey] ?? false;

export const getErrorLoadingIndividualBookmarksByQuery = (query: IndividualBookmarkQuery) => (state: RootState) =>
  state.bookmarks.individualBookmarksByQuery.error[query.cacheKey] ?? null;

/** The page whose request for this search failed last, or null when the last request did not fail. */
export const getFailedIndividualBookmarkPageByQuery = (query: IndividualBookmarkQuery) =>
  (state: RootState): FailedIndividualBookmarkPage | null =>
    state.bookmarks.individualBookmarksByQuery.failedPage[query.cacheKey] ?? null;

// individualBookmarkByIndividualId
/** The bookmark: bookmarked. Null: not bookmarked. Undefined: not asked yet. */
export const getIndividualBookmarkByIndividualId = (individualId?: Guid) =>
  (state: RootState): BookmarkedIndividualJSON | null | undefined =>
    individualId ? state.bookmarks.individualBookmarkByIndividualId.entries[individualId.value] : undefined;

/** True when the last ask about this person failed, and nothing has asked again. */
export const getHasIndividualBookmarkStatusFailed = (individualId?: Guid) => (state: RootState): boolean =>
  individualId ? state.bookmarks.individualBookmarkByIndividualId.failed[individualId.value] ?? false : false;

// teamBookmarkByTeamId
/** The bookmark: bookmarked. Null: not bookmarked. Undefined: not asked yet. */
export const getTeamBookmarkByTeamId = (teamId?: Guid) =>
  (state: RootState): BookmarkedTeamJSON | null | undefined =>
    teamId ? state.bookmarks.teamBookmarkByTeamId.entries[teamId.value] : undefined;

/** True when the last ask about this team failed, and nothing has asked again. */
export const getHasTeamBookmarkStatusFailed = (teamId?: Guid) => (state: RootState): boolean =>
  teamId ? state.bookmarks.teamBookmarkByTeamId.failed[teamId.value] ?? false : false;

// #endregion
// #endregion
